import { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getFriendsPageInfos } from "../../functions/user";
import {
  getCustomLists,
  createCustomList,
  updateCustomList,
  deleteCustomList,
  addFriendToList,
  removeFriendFromList,
  addAdminToList,
  removeAdminFromList,
} from "../../functions/customLists";
import { uploadImages } from "../../functions/uploadImages";
import dataURItoBlob from "../../helpers/dataURItoBlob";
import GroupPosts from "../../components/groups/GroupPosts";
import "./style.css";

const MEMBERS_PREVIEW_LIMIT = 5;

export default function CustomLists({ showMembers = true }) {
  const { user } = useSelector((state) => ({ ...state }));
  const navigate = useNavigate();
  const [ownedLists, setOwnedLists] = useState([]);
  const [memberLists, setMemberLists] = useState([]);
  const [availableFriends, setAvailableFriends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newListName, setNewListName] = useState("");
  const [newListDescription, setNewListDescription] = useState("");
  const [editingList, setEditingList] = useState(null);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [showFriendsList, setShowFriendsList] = useState(null);
  const [showAdminManagement, setShowAdminManagement] = useState(null);
  const [makeAdminOnAdd, setMakeAdminOnAdd] = useState(false);
  const [friendsSearchQuery, setFriendsSearchQuery] = useState("");
  const [selectedFriendIds, setSelectedFriendIds] = useState([]);
  const [isAddingSelectedFriends, setIsAddingSelectedFriends] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [fullMembersListId, setFullMembersListId] = useState(null);
  const [memberToRemove, setMemberToRemove] = useState(null);
  const [isRemovingMember, setIsRemovingMember] = useState(false);
  
  // Group picture upload states
  const [, setGroupPicture] = useState("");
  const [groupPicturePreview, setGroupPicturePreview] = useState("");
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const groupPictureInputRef = useRef(null);
  
  // Edit group picture states
  const [editGroupPicture, setEditGroupPicture] = useState("");
  const [editGroupPicturePreview, setEditGroupPicturePreview] = useState("");
  const [uploadingEditPicture, setUploadingEditPicture] = useState(false);
  const editGroupPictureInputRef = useRef(null);

  const loadData = useCallback(async (isMounted = () => true) => {
    try {
      if (!isMounted()) return;
      setLoading(true);
      setError("");

      // Load custom lists and friends data
      const [customListsResponse, friendsResponse] = await Promise.all([
        getCustomLists(user.token),
        getFriendsPageInfos(user.token)
      ]);

      if (!isMounted()) return;

      if (customListsResponse.status === "ok") {
        // Normalize list IDs - ensure all lists have an 'id' field (use _id if id doesn't exist)
        const normalizeList = (list) => {
          if (!list) return null;
          return {
            ...list,
            id: list.id || list._id || list.listId
          };
        };
        
        const ownedListsNormalized = (customListsResponse.data.ownedLists || []).map(normalizeList).filter(Boolean);
        const memberListsNormalized = (customListsResponse.data.memberLists || []).map(normalizeList).filter(Boolean);
        
        setOwnedLists(ownedListsNormalized);
        setMemberLists(memberListsNormalized);
      } else {
        setError(customListsResponse.message);
      }

      if (friendsResponse.status === "ok") {
        setAvailableFriends(friendsResponse.data.friends || []);
      }
    } catch (err) {
      if (isMounted()) {
        setError("Failed to load data");
      }
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  }, [user.token]);

  useEffect(() => {
    let mounted = true;
    
    const loadDataSafe = async () => {
      if (mounted) {
        await loadData(() => mounted);
      }
    };
    
    loadDataSafe();
    
    return () => {
      mounted = false;
    };
  }, [loadData]);

  const handleCreateList = async () => {
    if (newListName.trim()) {
      try {
        setUploadingPicture(true);
        let groupPictureUrl = "";

        // Upload group picture if selected
        if (groupPicturePreview) {
          const img = dataURItoBlob(groupPicturePreview);
          const path = `${user.username}/group_pictures`;
          let formData = new FormData();
          formData.append("path", path);
          formData.append("file", img);
          
          const uploadResponse = await uploadImages(formData, path, user.token);
          if (Array.isArray(uploadResponse) && uploadResponse.length > 0) {
            groupPictureUrl = uploadResponse[0].url;
          } else {
            setError("Failed to upload group picture");
            setUploadingPicture(false);
            return;
          }
        }

        const response = await createCustomList(
          {
            name: newListName.trim(),
            description: newListDescription.trim(),
            groupPicture: groupPictureUrl,
          },
          user.token
        );

        if (response.status === "ok") {
          // Add the group to the list first
          setOwnedLists([response.data, ...ownedLists]);
          
          // Reset form
          setNewListName("");
          setNewListDescription("");
          setGroupPicture("");
          setGroupPicturePreview("");
          setShowCreateForm(false);
        } else {
          setError(response.message);
        }
      } catch (err) {
        setError("Failed to create list");
      } finally {
        setUploadingPicture(false);
      }
    }
  };

  const handleDeleteList = (listId) => {
    setShowDeleteConfirm(listId);
  };

  const confirmDeleteList = async (listId) => {
    try {
      const response = await deleteCustomList(listId, user.token);
      if (response.status === "ok") {
        setOwnedLists(ownedLists.filter(list => list.id !== listId));
        setShowDeleteConfirm(null);
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError("Failed to delete list");
    }
  };

  const cancelDeleteList = () => {
    setShowDeleteConfirm(null);
  };

  const handleEditList = (list) => {
    console.log("Edit button clicked for list:", list);
    setEditingList(list.id);
    setEditName(list.name);
    setEditDescription(list.description || "");
    setEditGroupPicture(list.groupPicture || "");
    setEditGroupPicturePreview(list.groupPicture || "");
    setError(""); // Clear any previous errors
  };

  const handleSaveEdit = async () => {
    if (editName.trim()) {
      try {
        setUploadingEditPicture(true);
        let groupPictureUrl = editGroupPicture; // Keep existing picture if no new one selected

        // Upload new group picture if selected
        if (editGroupPicturePreview && editGroupPicturePreview !== editGroupPicture) {
          const img = dataURItoBlob(editGroupPicturePreview);
          const path = `${user.username}/group_pictures`;
          let formData = new FormData();
          formData.append("path", path);
          formData.append("file", img);
          
          const uploadResponse = await uploadImages(formData, path, user.token);
          if (Array.isArray(uploadResponse) && uploadResponse.length > 0) {
            groupPictureUrl = uploadResponse[0].url;
          } else {
            setError("Failed to upload group picture");
            setUploadingEditPicture(false);
            return;
          }
        }

        console.log("Saving edit for list:", editingList);
        console.log("New name:", editName.trim());
        console.log("New description:", editDescription.trim());
        console.log("New group picture:", groupPictureUrl);
        
        const response = await updateCustomList(
          editingList,
          {
            name: editName.trim(),
            description: editDescription.trim(),
            groupPicture: groupPictureUrl,
          },
          user.token
        );

        console.log("Update response:", response);

        if (response.status === "ok") {
          // Update the list in the appropriate array
          setOwnedLists(ownedLists.map(list => 
            list.id === editingList ? response.data : list
          ));
          setMemberLists(memberLists.map(list => 
            list.id === editingList ? response.data : list
          ));
          setEditingList(null);
          setEditName("");
          setEditDescription("");
          setEditGroupPicture("");
          setEditGroupPicturePreview("");
          setError(""); // Clear any previous errors
        } else {
          setError(response.message || "Failed to update group");
        }
      } catch (err) {
        console.error("Error updating list:", err);
        setError("Failed to update group. Please try again.");
      } finally {
        setUploadingEditPicture(false);
      }
    } else {
      setError("Group name cannot be empty");
    }
  };

  const handleCancelEdit = () => {
    setEditingList(null);
    setEditName("");
    setEditDescription("");
    setEditGroupPicture("");
    setEditGroupPicturePreview("");
  };

  const handleShowFriends = (listId) => {
    const next = showFriendsList === listId ? null : listId;
    setShowFriendsList(next);
    setMakeAdminOnAdd(false);
    setFriendsSearchQuery("");
    setSelectedFriendIds([]);
    setIsAddingSelectedFriends(false);
  };

  const closeAddFriendsModal = () => {
    setShowFriendsList(null);
    setMakeAdminOnAdd(false);
    setFriendsSearchQuery("");
    setSelectedFriendIds([]);
    setIsAddingSelectedFriends(false);
  };

  const toggleFriendSelection = (friendId) => {
    if (isAddingSelectedFriends) return;
    setSelectedFriendIds((prev) =>
      prev.includes(friendId)
        ? prev.filter((id) => id !== friendId)
        : [...prev, friendId]
    );
  };

  const handleAddSelectedFriendsToList = async (listId) => {
    if (!listId || selectedFriendIds.length === 0 || isAddingSelectedFriends) {
      return;
    }

    try {
      setIsAddingSelectedFriends(true);
      let lastUpdatedList = null;
      const errors = [];

      for (const friendId of selectedFriendIds) {
        const response = await addFriendToList(listId, friendId, user.token);
        if (response.status === "ok") {
          lastUpdatedList = response.data;
          if (makeAdminOnAdd) {
            await handleMakeAdmin(listId, friendId);
          }
        } else {
          errors.push(response.message || "Failed to add friend");
        }
      }

      if (lastUpdatedList) {
        setOwnedLists((prev) =>
          prev.map((list) => (list.id === listId ? lastUpdatedList : list))
        );
        setMemberLists((prev) =>
          prev.map((list) => (list.id === listId ? lastUpdatedList : list))
        );
        // Refresh lists so admin promotions are reflected after batch add.
        if (makeAdminOnAdd) {
          const refreshed = await getCustomLists(user.token);
          if (refreshed?.status === "ok") {
            setOwnedLists(refreshed.data.ownedLists || []);
            setMemberLists(refreshed.data.memberLists || []);
            const refreshedGroup = [
              ...(refreshed.data.ownedLists || []),
              ...(refreshed.data.memberLists || []),
            ].find((list) => list.id === listId || list._id === listId);
            if (refreshedGroup && selectedGroup?.id === listId) {
              setSelectedGroup({
                ...refreshedGroup,
                id: refreshedGroup.id || refreshedGroup._id,
              });
            }
          }
        } else if (selectedGroup?.id === listId) {
          setSelectedGroup(lastUpdatedList);
        }
        closeAddFriendsModal();
      } else {
        setError(errors[0] || "Failed to add selected friends");
      }
    } catch (err) {
      setError("Failed to add selected friends");
    } finally {
      setIsAddingSelectedFriends(false);
    }
  };

  const handleRemoveFriendFromList = async (listId, friendId) => {
    try {
      const normalizedFriendId =
        typeof friendId === 'object' && friendId !== null
          ? friendId._id || friendId.id
          : friendId;
      const response = await removeFriendFromList(listId, normalizedFriendId, user.token);
      if (response.status === "ok") {
        // Update the list in the appropriate array
        setOwnedLists(ownedLists.map(list => 
          list.id === listId ? response.data : list
        ));
        setMemberLists(memberLists.map(list => 
          list.id === listId ? response.data : list
        ));
        return true;
      } else {
        setError(response.message);
        return false;
      }
    } catch (err) {
      setError("Failed to remove friend from list");
      return false;
    }
  };

  const getMemberDisplayName = (member) => {
    if (!member) return "this member";
    return (
      member.name ||
      `${member.first_name || ""} ${member.last_name || ""}`.trim() ||
      member.username ||
      "this member"
    );
  };

  const requestRemoveMember = (list, member) => {
    setMemberToRemove({ list, member });
  };

  const cancelRemoveMember = () => {
    if (isRemovingMember) return;
    setMemberToRemove(null);
  };

  const confirmRemoveMember = async () => {
    if (!memberToRemove || isRemovingMember) return;
    const { list, member } = memberToRemove;
    setIsRemovingMember(true);
    try {
      const removed = await handleRemoveFriendFromList(list.id, member._id || member.id);
      if (removed) {
        setMemberToRemove(null);
      }
    } finally {
      setIsRemovingMember(false);
    }
  };

  const handleShowAdminManagement = (listId) => {
    setShowAdminManagement(showAdminManagement === listId ? null : listId);
  };

  const handleMakeAdmin = async (listId, friendId) => {
    try {
      const response = await addAdminToList(listId, friendId, user.token);
      if (response.status === "ok") {
        // Update the list in the appropriate array with force refresh
        const updatedList = { ...response.data, lastUpdated: Date.now() };
        setOwnedLists(ownedLists.map(list => 
          list.id === listId ? updatedList : list
        ));
        setMemberLists(memberLists.map(list => 
          list.id === listId ? updatedList : list
        ));
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError("Failed to make admin");
    }
  };

  const handleRemoveAdmin = async (listId, friendId) => {
    try {
      const normalizedFriendId =
        typeof friendId === 'object' && friendId !== null
          ? friendId._id || friendId.id
          : friendId;
      const response = await removeAdminFromList(listId, normalizedFriendId, user.token);
      if (response.status === "ok") {
        // Update the list in the appropriate array with force refresh
        const updatedList = { ...response.data, lastUpdated: Date.now() };
        setOwnedLists(ownedLists.map(list => 
          list.id === listId ? updatedList : list
        ));
        setMemberLists(memberLists.map(list => 
          list.id === listId ? updatedList : list
        ));
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError("Failed to remove admin");
    }
  };

  const normalizeId = (value) => {
    if (!value) return null;
    if (typeof value === 'string') return value;
    if (typeof value === 'object' && value._id) {
      return typeof value._id === 'string' ? value._id : value._id.toString();
    }
    if (typeof value === 'object' && value.id) {
      return typeof value.id === 'string' ? value.id : value.id.toString();
    }
    if (typeof value.toString === 'function') {
      const str = value.toString();
      return str === '[object Object]' ? null : str;
    }
    return null;
  };

  const getMemberRole = (list, member) => {
    if (!list || !member) return 'member';
    if (member.role === 'admin') return 'admin';
    const memberId = normalizeId(member);
    const ownerId = normalizeId(list.owner);
    if (memberId && ownerId && memberId === ownerId) return 'admin';
    if (Array.isArray(list.admins) && memberId) {
      const isAdmin = list.admins.some((admin) => normalizeId(admin) === memberId);
      if (isAdmin) return 'admin';
    }
    return 'member';
  };

  const getRoleLabel = (role) => (role === 'admin' ? 'Admin' : 'Member');

  const computeMembersList = (list) => {
    if (!list) return [];

    const membersMap = new Map();

    const upsertMember = (value, defaultRole) => {
      const memberId = normalizeId(value);
      if (!memberId) return;

      const memberObject =
        typeof value === "object" && value !== null
          ? value
          : { _id: memberId };

      const incomingRole =
        memberObject.role === "admin" || memberObject.role === "member"
          ? memberObject.role
          : defaultRole;

      const existing = membersMap.get(memberId);
      const resolvedRole =
        existing?.role === "admin" || incomingRole === "admin" ? "admin" : "member";

      membersMap.set(memberId, {
        ...(existing || {}),
        ...memberObject,
        _id: memberObject._id || existing?._id || memberId,
        role: resolvedRole,
      });
    };

    (list.members || []).forEach((member) => upsertMember(member, "member"));
    (list.admins || []).forEach((admin) => upsertMember(admin, "admin"));
    upsertMember(list.owner, "admin");

    return Array.from(membersMap.values()).filter(
      (member) => member && (member._id || member.id)
    );
  };

  const renderMemberItem = (list, member, { canRemove = false } = {}) => {
    if (!member || !(member._id || member.id)) return null;
    const memberKey = normalizeId(member) || member._id || member.id;
    return (
      <div key={memberKey} className="member_item">
        <div className="member_info">
          <img
            src={member.picture || '/images/default_avatar.png'}
            alt={member.name || `${member.first_name || ''} ${member.last_name || ''}`}
            onError={(e) => {
              e.target.src = '/images/default_avatar.png';
            }}
          />
          <div className="member_details">
            <div className="member_name_row">
              <span className="member_name">
                {member.name || `${member.first_name || ''} ${member.last_name || ''}`.trim() || 'Unknown User'}
              </span>
              <div className={`list_role_badge ${getMemberRole(list, member)}`}>
                {getRoleLabel(getMemberRole(list, member))}
              </div>
            </div>
            <span className="member_username">@{member.username || 'unknown'}</span>
          </div>
        </div>
        {canRemove && (
          <button
            className="remove_member_btn hover3"
            onClick={(e) => {
              e.stopPropagation();
              requestRemoveMember(list, member);
            }}
            title="Remove from list"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        )}
      </div>
    );
  };

  const renderMembersSection = (list, { canRemove = false } = {}) => {
    const members = computeMembersList(list);
    if (!showMembers || members.length === 0) return null;

    const visibleMembers = members.slice(0, MEMBERS_PREVIEW_LIMIT);
    const remainingCount = members.length - visibleMembers.length;

    return (
      <div className="current_members">
        <h5 className="members_title">Members ({members.length})</h5>
        <div className="members_list">
          {visibleMembers.map((member) => renderMemberItem(list, member, { canRemove }))}
        </div>
        {remainingCount > 0 && (
          <button
            type="button"
            className="members_more_btn"
            onClick={(e) => {
              e.stopPropagation();
              setFullMembersListId(list.id);
            }}
          >
            +{remainingCount} more
          </button>
        )}
      </div>
    );
  };

  const renderFullMembersModal = () => {
    if (!fullMembersListId) return null;

    const allLists = [...ownedLists, ...memberLists];
    const list = allLists.find((item) => item.id === fullMembersListId);
    if (!list) return null;

    const members = computeMembersList(list);
    const canRemove = ownedLists.some((item) => item.id === list.id);

    return (
      <div
        className="add_members_dialog_overlay"
        onClick={() => setFullMembersListId(null)}
      >
        <div
          className="add_members_dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="full-members-dialog-title"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="add_members_dialog_header">
            <div className="add_members_dialog_header_text">
              <h4 id="full-members-dialog-title">Members ({members.length})</h4>
              <p>Everyone in &quot;{list.name}&quot;</p>
            </div>
            <button
              type="button"
              className="add_members_dialog_close"
              onClick={() => setFullMembersListId(null)}
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <div className="add_members_dialog_list">
            <div className="members_list">
              {members.map((member) => renderMemberItem(list, member, { canRemove }))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleHeaderClick = (listId) => {
    // Only navigate if showMembers is false (we're on home page)
    if (!showMembers) {
      navigate('/friends/custom-lists');
    }
  };

  const handleViewPosts = (group) => {
    setSelectedGroup(group);
  };

  const handleBackToGroups = () => {
    setSelectedGroup(null);
  };

  // Group picture upload handler
  const handleGroupPictureChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError("Please select an image file");
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError("Image size must be less than 5MB");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setGroupPicturePreview(event.target.result);
    };
    reader.readAsDataURL(file);
  };

  const removeGroupPicture = () => {
    setGroupPicture("");
    setGroupPicturePreview("");
    if (groupPictureInputRef.current) {
      groupPictureInputRef.current.value = '';
    }
  };

  // Edit group picture handlers
  const handleEditGroupPictureChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError("Please select an image file");
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError("Image size must be less than 5MB");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setEditGroupPicturePreview(event.target.result);
    };
    reader.readAsDataURL(file);
  };

  const removeEditGroupPicture = () => {
    setEditGroupPicture("");
    setEditGroupPicturePreview("");
    if (editGroupPictureInputRef.current) {
      editGroupPictureInputRef.current.value = '';
    }
  };

  const renderAddMembersModal = () => {
    if (!showFriendsList) return null;

    const allLists = [...ownedLists, ...memberLists];
    const list = allLists.find((l) => l.id === showFriendsList);
    if (!list) return null;

    const currentMemberIds = new Set(
      computeMembersList(list).map((member) => normalizeId(member)).filter(Boolean)
    );
    const friendsToShow = availableFriends.filter(
      (friend) => !currentMemberIds.has(normalizeId(friend))
    );
    const search = friendsSearchQuery.trim().toLowerCase();
    const filteredFriends = search
      ? friendsToShow.filter((friend) => {
          const name = (
            friend.name ||
            `${friend.first_name || ""} ${friend.last_name || ""}`
          )
            .trim()
            .toLowerCase();
          const username = String(friend.username || "").toLowerCase();
          return name.includes(search) || username.includes(search);
        })
      : friendsToShow;

    return (
      <div
        className="add_members_dialog_overlay"
        onClick={() => {
          if (!isAddingSelectedFriends) closeAddFriendsModal();
        }}
      >
        <div
          className="add_members_dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="add-members-dialog-title"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="add_members_dialog_header">
            <div className="add_members_dialog_header_text">
              <h4 id="add-members-dialog-title">Add Members</h4>
              <p>Select friends to add to &quot;{list.name}&quot;</p>
            </div>
            <button
              type="button"
              className="add_members_dialog_close"
              onClick={closeAddFriendsModal}
              disabled={isAddingSelectedFriends}
              aria-label="Close"
            >
              ×
            </button>
          </div>

          <div className="add_members_dialog_search">
            <input
              type="search"
              className="add_members_dialog_search_input"
              placeholder="Search friends..."
              value={friendsSearchQuery}
              onChange={(e) => setFriendsSearchQuery(e.target.value)}
              disabled={isAddingSelectedFriends}
              aria-label="Search friends"
            />
          </div>

          <div className="add_members_dialog_admin_option">
            <label className="add_members_dialog_admin_label">
              <input
                type="checkbox"
                checked={makeAdminOnAdd}
                onChange={(e) => setMakeAdminOnAdd(e.target.checked)}
                disabled={isAddingSelectedFriends}
              />
              <span>Make admin when adding</span>
            </label>
          </div>

          <div
            className="add_members_dialog_list"
            role="listbox"
            aria-multiselectable="true"
          >
            {filteredFriends.length > 0 ? (
              filteredFriends.map((friend) => {
                const friendId = friend._id;
                const isSelected = selectedFriendIds.includes(friendId);
                const displayName =
                  friend.name ||
                  `${friend.first_name || ""} ${friend.last_name || ""}`.trim() ||
                  "Friend";
                return (
                  <button
                    key={friendId}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    className={`add_members_dialog_item ${
                      isSelected ? "is_selected" : ""
                    }`}
                    onClick={() => toggleFriendSelection(friendId)}
                    disabled={isAddingSelectedFriends}
                  >
                    <span
                      className={`add_members_dialog_checkbox ${
                        isSelected ? "checked" : ""
                      }`}
                      aria-hidden="true"
                    >
                      {isSelected ? "✓" : ""}
                    </span>
                    <img
                      src={friend.picture || "/images/default_profile.png"}
                      alt=""
                      className="add_members_dialog_avatar"
                    />
                    <span className="add_members_dialog_info">
                      <span className="add_members_dialog_name">
                        {displayName}
                      </span>
                      {friend.username && (
                        <span className="add_members_dialog_username">
                          @{friend.username}
                        </span>
                      )}
                    </span>
                  </button>
                );
              })
            ) : (
              <div className="add_members_dialog_empty">
                <div className="add_members_dialog_empty_icon">👥</div>
                <div className="add_members_dialog_empty_title">
                  {friendsToShow.length === 0
                    ? computeMembersList(list).length > 0
                      ? "All friends already added"
                      : "No friends available"
                    : "No friends match your search"}
                </div>
                <div className="add_members_dialog_empty_subtitle">
                  {friendsToShow.length === 0
                    ? computeMembersList(list).length > 0
                      ? "Everyone you know is already in this group"
                      : "Add some friends to get started"
                    : "Try a different name or username"}
                </div>
              </div>
            )}
          </div>

          <div className="add_members_dialog_footer">
            <button
              type="button"
              className="add_members_dialog_cancel"
              onClick={closeAddFriendsModal}
              disabled={isAddingSelectedFriends}
            >
              Cancel
            </button>
            <button
              type="button"
              className="add_members_dialog_submit"
              onClick={() => handleAddSelectedFriendsToList(list.id)}
              disabled={
                isAddingSelectedFriends || selectedFriendIds.length === 0
              }
            >
              {isAddingSelectedFriends
                ? "Adding…"
                : selectedFriendIds.length > 0
                  ? `Add Members (${selectedFriendIds.length})`
                  : "Add Members"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Show group posts if a group is selected
  if (selectedGroup) {
    const allLists = [...ownedLists, ...memberLists];
    const freshGroup =
      allLists.find((list) => list.id === selectedGroup.id) || selectedGroup;

    return (
      <>
        <GroupPosts
          group={freshGroup}
          onBack={handleBackToGroups}
          onInvite={(group) => handleShowFriends(group.id)}
        />
        {renderAddMembersModal()}
      </>
    );
  }

  if (loading) {
    return (
      <div className="friends_right_wrap">
        <div className="friends_left_header">
          <h3>Groups </h3>
        </div>
        <div className="loading">Loading groups...</div>
      </div>
    );
  }

  return (
    <>
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          
          @keyframes slideInUp {
            from { 
              opacity: 0;
              transform: translateY(30px) scale(0.95);
            }
            to { 
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }
        `}
      </style>
      <div className="friends_right_wrap">
      <div className="friends_left_header">
        <h3>Groups </h3>
        <button 
          className="blue_btn"
          onClick={() => setShowCreateForm(!showCreateForm)}
        >
          {showCreateForm ? "Cancel" : "Create Group"}
        </button>
      </div>

      {error && (
        <div className="error_message" style={{ 
          padding: "10px", 
          background: "#fee", 
          color: "#c33", 
          borderRadius: "6px", 
          marginBottom: "16px" 
        }}>
          {error}
        </div>
      )}



      {showCreateForm && (
        <div className="create_list_form">
          <div className="input_wrap">
            <input
              type="text"
              placeholder="Group name"
              value={newListName}
              onChange={(e) => setNewListName(e.target.value)}
              className="input_field"
            />
          </div>
          <div className="input_wrap">
            <input
              type="text"
              placeholder="Description (optional)"
              value={newListDescription}
              onChange={(e) => setNewListDescription(e.target.value)}
              className="input_field"
            />
          </div>
          
          {/* Group Picture Upload Section */}
          <div className="group_picture_upload">
            <h4>Group Profile Picture (Optional)</h4>
            <div className="picture_upload_area">
              {groupPicturePreview ? (
                <div className="picture_preview">
                  <img src={groupPicturePreview} alt="Group preview" />
                  <button 
                    type="button" 
                    className="remove_picture_btn"
                    onClick={removeGroupPicture}
                  >
                    ×
                  </button>
                </div>
              ) : (
                <div 
                  className="upload_placeholder"
                  onClick={() => groupPictureInputRef.current?.click()}
                >
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 19V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2z"/>
                    <circle cx="8.5" cy="8.5" r="1.5"/>
                    <path d="M21 15l-5-5L5 21"/>
                  </svg>
                  <p>Click to upload group picture</p>
                  <span>JPG, PNG, GIF up to 5MB</span>
                </div>
              )}
              <input
                ref={groupPictureInputRef}
                type="file"
                accept="image/*"
                onChange={handleGroupPictureChange}
                style={{ display: 'none' }}
              />
            </div>
          </div>
          
          <button 
            className="blue_btn"
            onClick={handleCreateList}
            disabled={!newListName.trim() || uploadingPicture}
          >
            {uploadingPicture ? "Creating Group..." : "Create Group"}
          </button>
        </div>
      )}

      <div className="custom_lists_container">
        {/* Owned Lists Section */}
        {ownedLists.length > 0 && (
          <div className="lists_section">
            <h4 className="section_title">Your Lists ({ownedLists.length})</h4>
            {ownedLists.map((list) => (
              <div key={list.id} className="custom_list_item">

                {/* Your existing list rendering code */}
                {editingList === list.id ? (
                  // Edit Mode
                  <div className="edit_list_form">
                    <div style={{ 
                      background: '#e3f2fd', 
                      padding: '8px 12px', 
                      borderRadius: '6px', 
                      marginBottom: '16px',
                      border: '1px solid #2196f3',
                      color: '#1976d2',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}>
                      ✏️ Editing: {list.name}
                    </div>
                    <div className="input_wrap">
                      <input
                        type="text"
                        placeholder="Group name"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="input_field"
                      />
                    </div>
                    <div className="input_wrap">
                      <input
                        type="text"
                        placeholder="Description (optional)"
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                        className="input_field"
                      />
                    </div>
                    
                    {/* Edit Group Picture Upload Section */}
                    <div className="group_picture_upload">
                      <h4>Group Profile Picture</h4>
                      <div className="picture_upload_area">
                        {editGroupPicturePreview ? (
                          <div className="picture_preview">
                            <img src={editGroupPicturePreview} alt="Group preview" />
                            <button 
                              type="button" 
                              className="remove_picture_btn"
                              onClick={removeEditGroupPicture}
                            >
                              ×
                            </button>
                          </div>
                        ) : (
                          <div 
                            className="upload_placeholder"
                            onClick={() => editGroupPictureInputRef.current?.click()}
                          >
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M21 19V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2z"/>
                              <circle cx="8.5" cy="8.5" r="1.5"/>
                              <path d="M21 15l-5-5L5 21"/>
                            </svg>
                            <p>Click to upload new group picture</p>
                            <span>JPG, PNG, GIF up to 5MB</span>
                          </div>
                        )}
                        <input
                          ref={editGroupPictureInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleEditGroupPictureChange}
                          style={{ display: 'none' }}
                        />
                      </div>
                    </div>
                    
                    <div className="edit_actions">
                      <button 
                        className="blue_btn"
                        onClick={handleSaveEdit}
                        disabled={!editName.trim() || uploadingEditPicture}
                      >
                        {uploadingEditPicture ? "Saving..." : "Save"}
                      </button>
                      <button 
                        className="gray_btn"
                        onClick={handleCancelEdit}
                        disabled={uploadingEditPicture}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  // View Mode
                  <>
                    <div 
                      className="custom_list_header"
                      onClick={() => handleHeaderClick(list.id)}
                      style={{ cursor: !showMembers ? 'pointer' : 'default' }}
                    >
                      <div className="custom_list_info">
                        <div className="group_picture_display">
                          <img 
                            src={list.groupPicture || '/images/default_group.svg'} 
                            alt={list.name}
                            className="group_picture_img"
                          />
                        </div>
                        <h4 className="custom_list_name">{list.name}</h4>
                      </div>
                      <div className="custom_list_actions">
                        <button 
                          className="custom_list_btn hover3"
                          onClick={() => handleViewPosts(list)}
                          title="View posts"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                            <polyline points="14,2 14,8 20,8"></polyline>
                            <line x1="16" y1="13" x2="8" y2="13"></line>
                            <line x1="16" y1="17" x2="8" y2="17"></line>
                            <polyline points="10,9 9,9 8,9"></polyline>
                          </svg>
                        </button>
                        {showMembers && (
                          <>
                            <button 
                              className="custom_list_btn hover3"
                              onClick={() => handleShowFriends(list.id)}
                              title="Add friends"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2">
                                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                                <circle cx="9" cy="7" r="4"></circle>
                                <line x1="19" y1="8" x2="19" y2="14"></line>
                                <line x1="22" y1="11" x2="16" y2="11"></line>
                              </svg>
                            </button>
                            <button 
                              className="custom_list_btn hover3"
                              onClick={() => handleShowAdminManagement(list.id)}
                              title="Manage admins"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2">
                                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path>
                              </svg>
                            </button>
                            <button 
                              className="custom_list_btn hover3"
                              onClick={(e) => {
                                e.stopPropagation();
                                console.log("Edit button clicked for list:", list);
                                handleEditList(list);
                              }}
                              title="Edit list"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2">
                                <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
                              </svg>
                            </button>
                            <button 
                              className="custom_list_btn hover3 delete_btn"
                              onClick={() => handleDeleteList(list.id)}
                              title="Delete list"
                              style={{
                                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                                color: 'white',
                                width: '44px',
                                height: '44px',
                                padding: '0',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)',
                                transition: 'all 0.3s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)';
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.5)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(239, 68, 68, 0.3)';
                              }}
                            >
                              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                                <path d="M3 6h18"></path>
                                <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                                <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                                <line x1="10" y1="11" x2="10" y2="17"></line>
                                <line x1="14" y1="11" x2="14" y2="17"></line>
                              </svg>
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    
                    {list.description && (
                      <p className="custom_list_description">{list.description}</p>
                    )}
                    <div className="custom_list_members">
                      {renderMembersSection(list, { canRemove: true })}

                      {/* No inline modal here - using global overlay modal below */}
                      {showMembers && computeMembersList(list).length === 0 && showFriendsList !== list.id && (
                          <div className="no_data">No friends added to this list yet</div>
                      )}

                      {/* Admin Management Modal */}
                      {showAdminManagement === list.id && (
                        <div className="admin_management_modal">
                          <div className="admin_management_header">
                            <h4>Manage Admins for "{list.name}"</h4>
                            <button 
                              className="close_btn"
                              onClick={() => setShowAdminManagement(null)}
                            >
                              ×
                            </button>
                          </div>
                          <div className="admin_management_content">
                            {computeMembersList(list).length > 0 ? (
                              computeMembersList(list).map((member) => {
                                if (!member || !member._id) return null;
                                const memberRole = getMemberRole(list, member);
                                return (
                                  <div key={member._id} className="admin_member_item">
                                    <div className="admin_member_info">
                                      <img 
                                        src={member.picture || '/images/default_avatar.png'} 
                                        alt={member.name || `${member.first_name || ''} ${member.last_name || ''}`} 
                                        onError={(e) => {
                                          e.target.src = '/images/default_avatar.png';
                                        }}
                                      />
                                      <div className="admin_member_details">
                                        <span className="admin_member_name">
                                          {member.name || `${member.first_name || ''} ${member.last_name || ''}`.trim() || 'Unknown User'}
                                        </span>
                                        <span className="admin_member_username">@{member.username || 'unknown'}</span>
                                      </div>
                                      <div className={`list_role_badge ${memberRole}`}>
                                        {getRoleLabel(memberRole)}
                                      </div>
                                    </div>
                                    <div className="admin_actions">
                                      {memberRole === 'member' && (
                                        <button 
                                          className="make_admin_btn blue_btn"
                                          onClick={() => handleMakeAdmin(list.id, member._id)}
                                        >
                                          Make Admin
                                        </button>
                                      )}
                                      {memberRole === 'admin' && (
                                        <button 
                                          className="remove_admin_btn gray_btn"
                                          onClick={() => handleRemoveAdmin(list.id, member._id)}
                                        >
                                          Remove Admin
                                        </button>
                                      )}
                                      {(() => {
                                        const ownerId = typeof list.owner === 'object' ? list.owner._id : list.owner;
                                        return member._id && ownerId && member._id.toString() === ownerId.toString();
                                      })() && (
                                        <span className="owner_label">List Owner</span>
                                      )}
                                    </div>
                                  </div>
                                );
                              })
                            ) : (
                              <div className="no_data">No members in this list</div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}

                 {/* Member Lists Section */}
         {memberLists.length > 0 && (
           <div className="lists_section">
             <h4 className="section_title">Lists You're Part Of ({memberLists.length})</h4>
             {memberLists.map((list) => (
               <div key={list.id} className="custom_list_item">

                 {/* Edit Mode for Member Lists */}
                 {editingList === list.id ? (
                   <div className="edit_list_form">
                     <div style={{ 
                       background: '#e3f2fd', 
                       padding: '8px 12px', 
                       borderRadius: '6px', 
                       marginBottom: '16px',
                       border: '1px solid #2196f3',
                       color: '#1976d2',
                       fontSize: '14px',
                       fontWeight: '500'
                     }}>
                       ✏️ Editing: {list.name}
                     </div>
                     <div className="input_wrap">
                       <input
                         type="text"
                         placeholder="Group name"
                         value={editName}
                         onChange={(e) => setEditName(e.target.value)}
                         className="input_field"
                       />
                     </div>
                     <div className="input_wrap">
                       <input
                         type="text"
                         placeholder="Description (optional)"
                         value={editDescription}
                         onChange={(e) => setEditDescription(e.target.value)}
                         className="input_field"
                       />
                     </div>
                     <div className="edit_actions">
                       <button 
                         className="blue_btn"
                         onClick={handleSaveEdit}
                         disabled={!editName.trim()}
                       >
                         Save
                       </button>
                       <button 
                         className="gray_btn"
                         onClick={handleCancelEdit}
                       >
                         Cancel
                       </button>
                     </div>
                   </div>
                 ) : (
                   // View Mode for Member Lists
                   <>
                     <div 
                       className="custom_list_header"
                       onClick={() => handleHeaderClick(list.id)}
                       style={{ cursor: !showMembers ? 'pointer' : 'default' }}
                     >
                       <div className="custom_list_info">
                         <h4 className="custom_list_name">{list.name}</h4>
                       </div>
                       <div className="custom_list_actions">
                         <button 
                           className="custom_list_btn hover3"
                           onClick={() => handleViewPosts(list)}
                           title="View posts"
                         >
                           <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2">
                             <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                             <polyline points="14,2 14,8 20,8"></polyline>
                             <line x1="16" y1="13" x2="8" y2="13"></line>
                             <line x1="16" y1="17" x2="8" y2="17"></line>
                             <polyline points="10,9 9,9 8,9"></polyline>
                           </svg>
                         </button>
                         {showMembers && (
                           <>
                             {list.userRole === 'admin' && (
                               <button 
                                 className="custom_list_btn hover3"
                                 onClick={() => handleShowFriends(list.id)}
                                 title="Add friends"
                               >
                                 <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2">
                                   <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                                   <circle cx="9" cy="7" r="4"></circle>
                                   <line x1="19" y1="8" x2="19" y2="14"></line>
                                   <line x1="22" y1="11" x2="16" y2="11"></line>
                                 </svg>
                               </button>
                             )}
                             <button 
                               className="custom_list_btn hover3"
                               onClick={(e) => {
                                 e.stopPropagation();
                                 console.log("Edit button clicked for member list:", list);
                                 handleEditList(list);
                               }}
                               title="Edit list"
                             >
                               <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2">
                                 <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
                               </svg>
                             </button>
                           </>
                         )}
                       </div>
                     </div>
                     {list.description && (
                       <p className="custom_list_description">{list.description}</p>
                     )}
                     <div className="custom_list_members">
                       {renderMembersSection(list, { canRemove: false })}
                     </div>
                   </>
                 )}
               </div>
             ))}
           </div>
         )}

         {/* No Lists Message */}
         {ownedLists.length === 0 && memberLists.length === 0 && (
           <div className="no_custom_lists">
             <h4>No Lists Yet</h4>
             <p>You haven't created or joined any lists yet.</p>
           </div>
         )}

         {renderAddMembersModal()}
         {renderFullMembersModal()}
       </div>

       {/* Delete Confirmation Modal */}
       {showDeleteConfirm && (
         <div className="delete_confirmation_overlay">
           <div className="delete_confirmation_modal">
             <div className="delete_confirmation_header">
               <h3>Delete Group</h3>
               <button 
                 className="close_btn"
                 onClick={cancelDeleteList}
               >
                 <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                   <line x1="18" y1="6" x2="6" y2="18"></line>
                   <line x1="6" y1="6" x2="18" y2="18"></line>
                 </svg>
               </button>
             </div>
             <div className="delete_confirmation_content">
               <div className="delete_warning_icon">
                 <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ff4757" strokeWidth="2">
                   <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                   <line x1="12" y1="9" x2="12" y2="13"></line>
                   <line x1="12" y1="17" x2="12.01" y2="17"></line>
                 </svg>
               </div>
               <h4>Are you sure you want to delete this group?</h4>
               <p>This action cannot be undone. All posts and member information in this group will be permanently deleted.</p>
             </div>
             <div className="delete_confirmation_actions">
               <button 
                 className="gray_btn"
                 onClick={cancelDeleteList}
               >
                 Cancel
               </button>
               <button 
                 className="red_btn"
                 onClick={() => confirmDeleteList(showDeleteConfirm)}
               >
                 Delete Group
               </button>
             </div>
           </div>
         </div>
       )}

       {memberToRemove && (
         <div
           className="delete_confirmation_overlay remove_member_confirmation_overlay"
           onClick={cancelRemoveMember}
         >
           <div
             className="delete_confirmation_modal"
             role="dialog"
             aria-modal="true"
             aria-labelledby="remove-member-title"
             onClick={(e) => e.stopPropagation()}
           >
             <div className="delete_confirmation_header">
               <h3 id="remove-member-title">Remove Member</h3>
               <button
                 type="button"
                 className="close_btn"
                 onClick={cancelRemoveMember}
                 disabled={isRemovingMember}
                 aria-label="Close"
               >
                 <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                   <line x1="18" y1="6" x2="6" y2="18"></line>
                   <line x1="6" y1="6" x2="18" y2="18"></line>
                 </svg>
               </button>
             </div>
             <div className="delete_confirmation_content">
               <div className="delete_warning_icon">
                 <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ff4757" strokeWidth="2">
                   <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                   <line x1="12" y1="9" x2="12" y2="13"></line>
                   <line x1="12" y1="17" x2="12.01" y2="17"></line>
                 </svg>
               </div>
               <h4>
                 Remove {getMemberDisplayName(memberToRemove.member)} from{" "}
                 {memberToRemove.list?.name || "this group"}?
               </h4>
               <p>They will no longer be a member of this group.</p>
             </div>
             <div className="delete_confirmation_actions">
               <button
                 type="button"
                 className="gray_btn"
                 onClick={cancelRemoveMember}
                 disabled={isRemovingMember}
               >
                 Cancel
               </button>
               <button
                 type="button"
                 className="red_btn"
                 onClick={confirmRemoveMember}
                 disabled={isRemovingMember}
               >
                 {isRemovingMember ? "Removing…" : "Remove"}
               </button>
             </div>
           </div>
         </div>
       )}
     </div>
     </>
   );
 } 